import React from "react";
import { User, Mail, Phone, Send } from "lucide-react";
import { useNavigate } from "react-router";
import type { Representative } from "../types";
import { useTranslation } from "react-i18next";

interface RepresentativeCardProps {
  representative: Representative;
  compact?: boolean;
}

export function RepresentativeCard({ representative, compact = false }: RepresentativeCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/representatives/${representative.id}`);
  };

  if (compact) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all w-full text-left"
      >
        {representative.photo ? (
          <img
            src={representative.photo}
            alt={representative.fullName}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
            <User size={20} className="text-indigo-600" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[14px] text-gray-900 truncate">{representative.fullName}</div>
          <div className="text-[12px] text-gray-500 truncate">{representative.position}</div>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="flex flex-col p-4 rounded-2xl bg-white border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all w-full"
    >
      <div className="flex items-start gap-3 mb-3">
        {representative.photo ? (
          <img
            src={representative.photo}
            alt={representative.fullName}
            className="w-14 h-14 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
            <User size={24} className="text-indigo-600" />
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[15px] font-medium text-gray-900 mb-0.5">
            {representative.fullName}
          </div>
          <div className="text-[13px] text-indigo-600">{representative.position}</div>
        </div>
      </div>

      {/* Contact hints */}
      <div className="flex items-center gap-2 text-[11px] text-gray-400">
        {(representative.publicPhone || representative.phone) && <Phone size={12} />}
        {representative.email && <Mail size={12} />}
        {representative.telegram && <Send size={12} />}
        <span className="ml-auto">{t("visitor.view_details")}</span>
      </div>
    </button>
  );
}
